import styles from './styles.module.scss';

import { useEffect, useMemo, useRef, useState } from 'react';

import { animate, motion, useDragControls, useMotionValue } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faFilter, faGripLines, faLocationDot, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import MapWithListingData from '../../../Misc/MapWithListingData/MapWithListingData';

import { BarLoader, PuffLoader, ScaleLoader } from 'react-spinners';
import Image from 'next/image';
import Head from 'next/head';
import axios from 'axios';
import errorMsg from '../../../Functions/errorMsg';

import AuctionListing from '../../../Account/Auctions/AuctionListing/AuctionListing';

import Filter from '../../../Filters/Filter';
import formatCurrency from '../../../Functions/formatCurrency';
import { useRouter } from 'next/router';
import { useSocket } from '../../../Context/socket';
import { isEqual } from 'lodash';

export default function Auctions() {

  const router = useRouter();

  // Loading state variables
  const [mapLoading, setMapLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  const [listingData, setListingData] = useState(null);
  const [selectedData, setSelectedData] = useState([]);
  const [showingData, setShowingData] = useState([]);

  const [availableObjects, setAvailableObjects] = useState(null);

  useEffect(() => {
    if(!dataLoading || listingData) return;

    async function getListingData() {
      const result = await axios.get(`${window.location.origin}/api/auctionHub/auctionListings`).catch((error) => errorMsg(error?.response?.data?.status || error.toString()))

      if(result && result.status === 200) {
        const data = result.data;
        setListingData(data.listings);
        setAvailableObjects(data.availableObjects)
      }
    }

    getListingData();

    setDataLoading(false);
    

  }, [dataLoading, listingData, availableObjects])

  useEffect(() => {
    if(!listingData) return;
    if(listingData?.length === 0) return;
    if(router?.query?.popup === 'sharedPost') {
      const getListingID = router.query._id;
      showClickedListing(getListingID)
    }
  }, [router, listingData])

   // Map Interaction Click Handler
  // =============================
  const [searchText, setSearchText] = useState('');
  const [selectedObjects, setSelectedObjects] = useState([]);

  useEffect(() => {
    if(!listingData) return;
    if(searchText.length > 0) {
      const filteredListings = listingData.filter((listing) => {
        const searchTextCheck = searchText.trim().toLowerCase();
        const title = listing.title.trim().toLowerCase();
        const description = listing.description.trim().toLowerCase();
        const category = listing.category.trim().toLowerCase();
    
        return (
          title.includes(searchTextCheck) ||
          description.includes(searchTextCheck) ||
          category.includes(searchTextCheck)
        );
      });

      setSelectedData(filteredListings);
      setShowingData(filteredListings);
    } else {
      const filteredListings = listingData.filter((listing) => selectedObjects.includes(listing.zip));
      setSelectedData(filteredListings);
      setShowingData(filteredListings);
    }
  }, [listingData, searchText])

  useEffect(() => {
    if (selectedObjects.length > 0) {
      const filteredListings = listingData.filter((listing) => selectedObjects.includes(listing.zip));
      setSelectedData(filteredListings);
      setShowingData(filteredListings);
    } else {
      if(searchText.length > 0) {
        const filteredListings = listingData.filter((listing) => {
          const searchTextCheck = searchText.trim().toLowerCase();
          const title = listing.title.trim().toLowerCase();
          const description = listing.description.trim().toLowerCase();
          const category = listing.category.trim().toLowerCase();
      
          return (
            title.includes(searchTextCheck) ||
            description.includes(searchTextCheck) ||
            category.includes(searchTextCheck)
          );
        });
  
        setSelectedData(filteredListings);
        setShowingData(filteredListings);
      } else {
        setSelectedData([]);
        setShowingData([]);
      }
    }
  }, [selectedObjects, listingData, searchText]);

  function handleObjectClick(obj) {
    setSelectedObjects(prevObjects => {
      if (prevObjects.includes(obj)) {
        return prevObjects.filter((curObj) => curObj !== obj);
      } else {
        return [...prevObjects, obj];
      }
    });
  }

  function isLoading() {
    if(mapLoading || dataLoading) return true;
    return false;
  }

  // Filter Stuff

  const [filterBar, setFilterBar] = useState(false);

  const showFilterBar = () => {
    if(isLoading()) return;
    setFilterBar(prevFilterBar => !prevFilterBar);
  }

  function filterApplyArray(newArray) {
    setShowingData(newArray);
  }

  function clearFilter() {
    setShowingData(selectedData);
  }

  // =============================
  // =============================

  const [clickedListing, showClickedListing] = useState(null);

  const [showListings, setShowListings] = useState(false);

  const memoizeListingContainer = useMemo(() => <ListingContainer isOpen={showListings} toggle={(value) => setShowListings(value)} noListings={listingData?.length === 0} listings={showingData} showFilterBar={showFilterBar} handleSearchMethod={(text) => setSearchText(text)} handleOpenListing={(listingID) => showClickedListing(listingID)} /> , [showingData, listingData, showListings]);
  const memoizeMap = useMemo(() => <MapWithListingData type='ZIP' availableObjects={availableObjects} listingData={listingData} handleMapLoaded={() => setMapLoading(false) } handleClick={handleObjectClick} /> , [listingData, availableObjects])
  
  const { auctionSocketConnected, auctionSocket } = useSocket();

  useEffect(() => {
    if(!auctionSocketConnected || !auctionSocket) return;

      auctionSocket.emit('join-auction-map');

    return () => {
      auctionSocket.emit('leave-auction-map');
    };
  }, [auctionSocketConnected, auctionSocket]);

  return (
    <section id={styles.wrapper}>
      { clickedListing && <AuctionListing listingID={clickedListing} closeListing={() => showClickedListing(null)} /> }
      <Filter isLoading={isLoading()} filterType="Auctions" unfilteredData={selectedData} allResults={showingData} filterBar={filterBar} showFilterBar={showFilterBar} filterApplyArray={filterApplyArray} clearFilter={clearFilter} />
      <Head>
        <title>Waco Local Auctions</title>
        <meta name="description" content="Browse and create auction posts free locally in Waco and the surrounding communities. Create the perfect opportunity to get the most out of your items." key="desc" />

        <meta property="og:title" content="Waco Local Auctions" />
        <meta property="og:description" content="Browse and create auction posts free locally in Waco and the surrounding communities. Create the perfect opportunity to get the most out of your items." />
      </Head>
      { isLoading() && <div id={styles.loading}> <ScaleLoader width={50} height={40} color='white' /> </div> }

      <div id={styles.controls}>
        
      </div>

      <section id={styles.content}>
        { listingData && <h1 id={styles.mapTypeText}>Auctions Map</h1> }
        { listingData && memoizeMap}
        { listingData && memoizeListingContainer}
      </section>

    </section>
  )
}
const ListingContainer = ({ isOpen, toggle, listings, showFilterBar, handleSearchMethod, handleOpenListing, noListings }) => {

  // Handling Listing Container Drag Controls & Animation
  // ====================================================

  const controls = useDragControls();
  const y = useMotionValue(isOpen ? 0 : 400);

  const onClickHandle = () => {
    if(isOpen) {
      toggle(false)
      animate(y, 400, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    } else {
      toggle(true);
      animate(y, 0, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    }
  }

  const onDragEnd = (_, info) => {
    const velocity = info.velocity.y;
    if (velocity > 0) {
      toggle(false);
      animate(y, 400, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    } else if (velocity < 0) {
      toggle(true);
      animate(y, 0, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    }
  };
  // ====================================================
  // ====================================================

  const displayContainerRef = useRef(null);

  const [showToTop, setShowToTop] = useState(false);

  const handleScroll = () => {
    if (!displayContainerRef || !displayContainerRef?.current) return;
    const position = displayContainerRef.current.scrollTop;

    if(position > 200) {
      setShowToTop(true);
    } else {
      setShowToTop(false);
    }

  };

  function handleScrollUp(e) {
    e.preventDefault();
    if (!displayContainerRef || !displayContainerRef?.current) return;
    displayContainerRef.current.scroll({top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    if(!displayContainerRef || !displayContainerRef?.current) return;

      displayContainerRef.current.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (!displayContainerRef || !displayContainerRef?.current) return;
      displayContainerRef.current.removeEventListener('scroll', handleScroll);
    };
  }, [displayContainerRef]);

  // Search Methods:

  const searchInputRef = useRef(null);

  function handleSearch() {
    if(!searchInputRef) return;
    if(!searchInputRef?.current) return;

    const text = searchInputRef.current.value;

    handleSearchMethod(text);
  }

  return (
    <motion.section id={styles.viewListings} style={{ y }} drag='y' dragElastic={0} dragConstraints={{ top: 0, bottom: 400 }} dragListener={false} dragControls={controls} onDragEnd={onDragEnd} >
      <div>
        <FontAwesomeIcon onClick={onClickHandle} icon={faGripLines} id={styles.handleContainerIcon} size='lg' onPointerDown={(e) => controls.start(e) } />
      </div>
      
      <div ref={displayContainerRef} id={styles.displayContainer}>
        { showToTop && <div id={styles.toTop}> <FontAwesomeIcon onClick={handleScrollUp} icon={faChevronUp} id={styles.icon} /> </div> }

        <section id={styles.queryMethods}>
          <div>
            <input ref={searchInputRef} placeholder='Search...' />
            <div onClick={handleSearch}> <FontAwesomeIcon icon={faMagnifyingGlass} id={styles.searchButton} /> </div>
          </div>
          <div>
            <FontAwesomeIcon icon={faFilter} id={styles.filter} onClick={showFilterBar} />
          </div>
          <div>
          </div>
        </section>

        {listings.length > 0 && listings.map((listing, index) => <DisplayCard key={index} listing={listing} handleOpenListing={handleOpenListing} />)}
        { noListings && listings.length === 0 && <h1>No Listings</h1> }
        { !noListings && listings.length === 0 && <h1>Select Map</h1> }

      </div>
    </motion.section>
  )
}

const DisplayCard = ({ listing, handleOpenListing }) => {

  const listingID = listing._id;

  const [coverLoaded, setCoverLoaded] = useState(false);
  const getCover = listing.images.findIndex((images) => images.position === 1);

  const { auctionSocketConnected, auctionSocket } = useSocket();

  const [loadingBids, setLoadingBids] = useState(true);
  const [bids, setBids] = useState(listing.bids.length > 0 ? listing.bids.sort((a, b) => b.bid - a.bid) : []);

  function handleIncomingBid(data) {
    const incomingAuctionID = data.auctionID;
    if(incomingAuctionID !== listingID) return;
    const username = data.username;
    const bid = data.bid;

    const newBid = { listingID, username, bid };
    const updatedBids = [...bids, newBid];

    updatedBids.sort((a, b) => b.bid - a.bid);

    setBids(updatedBids);
  }

  useEffect(() => {
    async function setupBidCAD() {
      if (!listingID) {
        return errorMsg("There was an error loading the bidding data for this auction, please try again later.");
      }
  
      try {
        const getAuctionBids = await axios.post(`${window.location.origin}/api/auctionHub/helpers/getAuctionBids`, {
          auctionID: listingID,
        });
  
        if (getAuctionBids.status === 200) {
          const result = getAuctionBids.data;
          setBids(result);
        } else {
          setBids(false);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setBids(false);
      }

      setLoadingBids(false);
    }
  
    setupBidCAD();

  }, [listingID]);

  function getHighestBid() {
    if(bids.length === 0) return { bid: listing.price }
    const sortedBidsArray = [...bids].sort((a, b) => b.bid - a.bid);
    if(!isEqual(sortedBidsArray, bids)) {
      setBids(sortedBidsArray);
    }

    return sortedBidsArray[0]
  }

  // Handle incoming new bid to update values

  useEffect(() => {
    if(!auctionSocket || !auctionSocketConnected) return;

    auctionSocket.on('incomingBidMap', handleIncomingBid);

    return () => {
      auctionSocket.off('incomingBidMap');
    };
  }, [auctionSocket, auctionSocketConnected])

  const { status, countdownText } = getCountdownText(listing.auctionDuration, listing.creationDate)

  console.log(status);

  return (
    <div className={styles.displayCard} onClick={() => handleOpenListing(listingID)}>
      { (loadingBids || !auctionSocketConnected) ? <section style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #c0c0c0', borderRadius: '15px'}}>
        <BarLoader color='black' />
      </section>
      :
      <>
      
      <div>
        <div style={{display: coverLoaded === false ? 'flex' : 'none'}}> <PuffLoader color="#424242" size={25} /></div>
        <Image sizes='auto auto' fill src={listing.images[getCover].url} alt={listing.images[getCover].url} onLoadingComplete={() => setCoverLoaded(true)} />
      </div>
      <div>
        <section>
          <h1>{listing.title}</h1>
          <h2>{listing.category}</h2>
        </section>
        <section>
          <h3><b>Bid:</b> {formatCurrency(getHighestBid().bid)}</h3>
          <h3><FontAwesomeIcon icon={faLocationDot} id={styles.icon} />{listing.zip}</h3>
        </section>

      </div>
      <div>
        <h1>
          { status === 'Counting Down' && countdownText }
          { status === 'Calculating winner...' && 'Pending' }
          { status === 'Auction Closed' && 'Closed' }
        </h1>
        <h2 style={{display: status === 'Counting Down' ? 'flex' : 'none'}}>Left</h2>
      </div>
      </>
      }
    </div>
  )
}


function getCountdownText(duration, created) {
  const durDays = parseInt(duration);

  const endingDate = new Date(Date.parse(created));
  endingDate.setDate(endingDate.getDate() + durDays);

  const currentDate = new Date();

  const totalMilliseconds = endingDate - currentDate;
  const totalSeconds = Math.floor(totalMilliseconds / 1000);

  console.log(totalSeconds);

  let status;
  let countdownText;
  
  if (totalSeconds <= 60) {
      countdownText = `${totalSeconds} Seconds${totalSeconds !== 1 ? 's' : ''}}`;
  } else if (totalSeconds <= 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      countdownText = `${minutes} Minute${minutes !== 1 ? 's' : ''}`;
  } else if (totalSeconds <= 86400) {
      const hours = Math.floor(totalSeconds / 3600);
      countdownText = `${hours} Hour${hours !== 1 ? 's' : ''}`;
  } else {
      const days = Math.floor(totalSeconds / 86400);
      countdownText = `${days} Day${days !== 1 ? 's' : ''}`;
  }

  if(totalSeconds <= 0 && totalSeconds >= -15) {
    status = "Calculating winner..."
  }

  if(totalSeconds < -15) {
    status = "Auction Closed"
  }

  if(totalSeconds > 0) {
    status = "Counting Down"
  }

  return { status, countdownText };
}